import {
  body, block, button, datalist, details, summary, div, h1, h2, h3, input, label as labelElement, link, option, p, palette, pre, select, span, svg, textarea,
  type Html,
} from "./html"

export type View = Html<any>
export type Region = View & {
  show(...children: (View | string)[]): Region
  add(...children: (View | string)[]): Region
}
export type Field = Html<HTMLInputElement> & {
  get(): string
  set(value: string): Field
  clear(): Field
}

export const group = (...children: (View | string)[]) => div(...children)
export function region(...children: (View | string)[]): Region {
  const result = group(...children) as Region
  result.show = (...next) => { result.replaceChildren(...next); return result }
  result.add = (...next) => { result.append(...next); return result }
  return result
}
export const text = (...children: (View | string)[]) => p(...children)
export const title = (...children: (View | string)[]) => h2(...children)
export const menuTitle = (...children: (View | string)[]) => h3(...children)
export const code = (value: unknown) => pre(typeof value === "string" ? value : JSON.stringify(value, null, 2))
export const card = (...children: (View | string)[]) => block(...children)
export function list(...items: (Html<any> | string)[]){
  return block(
    ...items.map((item, i)=> p(item)
    .style({
      borderTop: i > 0 ? `1px solid ${palette.border}` : "none",
      padding: "5px 10px",
      margin: "0",
      cursor: "pointer"
    })
  )).style({padding: "0"})
}


export const externalLink = link
export const action = button

export function downloadFile(name: string, content: Uint8Array, type = "application/octet-stream") {
  const copy = Uint8Array.from(content)
  const url = URL.createObjectURL(new Blob([copy.buffer], {type}))
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = name
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function uploadFiles(receive: (files: File[], directory: boolean) => void | Promise<void>) {
  const picker = (directory: boolean) => {
    const control = input().setAttribute("type", "file").setAttribute("multiple", "").style({display: "none"})
    if (directory) control.setAttribute("webkitdirectory", "")
    control.el.addEventListener("change", async () => {
      const files = [...(control.el.files ?? [])]
      control.el.value = ""
      if (files.length) await receive(files, directory)
    })
    return control
  }
  const files = picker(false)
  const folder = picker(true)
  return details(
    summary("Upload").style({cursor: "pointer", padding: "5px 10px"}),
    toolbar(
      action("Files", () => files.el.click()),
      action("Folder", () => folder.el.click()),
      files,
      folder,
    ).style({margin: "6px 0 0"}),
  )
}

export type IconName = "agent" | "arrow" | "clock" | "close" | "collapse" | "comment" | "copy" | "edit" | "expand" | "file" | "folder" | "grid" | "plus" | "save" | "sparkles" | "trash"
const iconPaths: Record<IconName, string[]> = {
  agent: ["M12 8V4H8", "M4 14a8 8 0 0 1 16 0v5H4v-5Z", "M9 14h.01", "M15 14h.01"],
  arrow: ["m9 18 6-6-6-6"],
  clock: ["M12 8v4l3 2", "M21 12a9 9 0 1 1-9-9 9 9 0 0 1 9 9Z"],
  close: ["m7 7 10 10", "M17 7 7 17"],
  collapse: ["M9 3v6H3", "m0 0-6-6", "M15 21v-6h6", "m-6 0 6 6"],
  comment: ["M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"],
  copy: ["M8 8h11v13H8Z", "M5 16H3V3h11v2"],
  edit: ["M12 20h9", "M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"],
  expand: ["M3 9V3h6", "m0 0-6 6", "M21 15v6h-6", "m0 0 6-6"],
  file: ["M6 2h8l4 4v16H6Z", "M14 2v5h5"],
  folder: ["M3 6h7l2 2h9v11H3Z"],
  grid: ["M4 4h6v6H4Z", "M14 4h6v6h-6Z", "M4 14h6v6H4Z", "M14 14h6v6h-6Z"],
  plus: ["M12 5v14", "M5 12h14"],
  save: ["M5 3h12l2 2v16H5Z", "M8 3v6h8V3", "M8 21v-7h8v7"],
  sparkles: ["m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z", "m5 14 .8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14Z"],
  trash: ["M4 7h16", "M9 7V4h6v3", "m6 7 1 14h10l1-14", "M10 11v6", "M14 11v6"],
}

export const icon = (name: IconName) => svg(iconPaths[name])
export const label = (name: IconName, value: string) => span(icon(name), span(value)).style({display: "inline-flex", alignItems: "center", gap: "8px"})
export const iconAction = (value: string, name: IconName, onclick: () => void) => action("", onclick)
  .replaceChildren(label(name, value))
  .setAttribute("aria-label", value)
export const circleAction = (value: string, name: IconName, onclick: () => void) => iconAction(value, name, onclick)
  .setAttribute("class", "etindi-circle-action")

export function field(value = "", options: {type?: string, autocomplete?: string} = {}): Field {
  const result = input() as Field
  result.el.value = value
  if (options.type) result.setAttribute("type", options.type)
  if (options.autocomplete) result.setAttribute("autocomplete", options.autocomplete)
  result.style({padding: "9px 11px", boxSizing: "border-box"})
  result.get = () => result.el.value
  result.set = next => { result.el.value = next; return result }
  result.clear = () => { result.el.value = ""; return result }
  return result
}

export function editor(value: string, lang = "txt") {

  const result = textarea(value)
  .style({
    fontFamily: ["js", "json"].includes (lang) ? "monospace" : "arial"
  })
  return Object.assign(result, {
    get: () => result.el.value,
  })
}

export function nav(title: string, onclick: () => void) {
  return button(title, onclick).style({color: palette.accent, border: "none", background: "transparent", padding: "0", margin: "0", font: "inherit", textAlign: "left", overflowWrap: "anywhere"})
}

export function dangerAction(title: string, onclick: () => void) {
  return span(title).style({float: "right", color: palette.dangerText, cursor: "pointer"}).onclick(onclick)
}

export function breadcrumbs(home: View, items: {title: string, open(): void}[]) {
  return text(home, ...items.map(item => span(icon("arrow"), nav(item.title, item.open)))).setAttribute("class", "etindi-breadcrumbs")
}

export function centeredTitle(value: string) {
  return h1(value).style({textAlign: "center"})
}

export function message(value = "") {
  const view = text(value)
  return {view, set: (next: string) => view.content(next)}
}

export function expandable(label: string, content: (view: Region) => void, subtitle = "") {
  const view = region()
  let loaded = false
  const result = details(summary(span(label), subtitle ? muted(subtitle).style({margin: "4px 0 0 18px"}) : "").style({cursor: "pointer"}), view).style({padding: "14px 0", overflowWrap: "anywhere"})
  result.el.addEventListener("toggle", () => {
    if (result.el.open && !loaded) { loaded = true; content(view) }
  })
  return result
}

export const workspace = () => region().style({maxWidth: "1080px", margin: "28px auto", lineHeight: "1.5", overflowWrap: "anywhere"}) as Region
export const explorerWorkspace = () => region().setAttribute("class", "etindi-explorer-workspace") as Region
export const narrowWorkspace = () => region().style({maxWidth: "440px", margin: "56px auto", lineHeight: "1.5"}) as Region
export const toolbar = (...items: View[]) => group(...items).style({display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center", margin: "16px 0"})
export const muted = (value: string) => text(value).style({color: palette.mutedText, fontSize: "13px"})
export const readable = (value: string) => text(value).style({whiteSpace: "pre-wrap", overflowWrap: "anywhere"})
export const wrappedCode = (value: unknown) => code(value).style({whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: "13px"})
export function chatBubble(role: string, content: View, own = false) {
  return group(
    own ? "" : text(role).style({margin: "0 0 6px", fontSize: "12px", fontWeight: "700", opacity: "0.8"}),
    content,
  ).style({
    maxWidth: "78%",
    margin: own ? "12px 0 12px auto" : "12px auto 12px 0",
    padding: "12px 16px",
    borderRadius: "0",
    background: own ? palette.accent : palette.surface,
    color: own ? palette.accentText : palette.text,
    border: `1px solid ${own ? palette.accent : palette.border}`,
  })
}
export function entryList(...items: View[]) {
  return card(...items.map((item, index) => group(item).style({
    padding: "10px 12px",
    borderBottom: index < items.length - 1 ? `1px solid ${palette.border}` : "none",
  }))).style({padding: "0"})
}
export const entryRow = (primary: View, secondary?: View) => group(primary, secondary ?? "").style({
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", minHeight: "36px",
})
export function navigation(items: {label: string, href: string, active: boolean, icon?: IconName}[]) {
  return toolbar(...items.map(item => externalLink(item.label, item.href).style({
    display: "inline-flex", alignItems: "center", gap: "8px", padding: "9px 12px 8px",
    borderBottom: item.active ? `2px solid ${palette.accent}` : "2px solid transparent",
    color: item.active ? palette.accent : palette.text, background: "transparent",
    fontWeight: item.active ? "600" : "normal",
  }).replaceChildren(item.icon ? label(item.icon, item.label) : item.label).setAttribute("aria-current", item.active ? "page" : "false"))).style({
    borderBottom: `1px solid ${palette.border}`, margin: "16px 0 24px",
  }).setAttribute("class", "etindi-module-navigation")
}

export function pageHeading(name: string, description: string, iconName?: IconName) {
  return group(
    title(iconName ? label(iconName, name) : name).style({margin: "0 0 4px"}),
    muted(description).style({margin: "0"}),
  ).style({marginBottom: "24px"}).setAttribute("class", "etindi-page-heading")
}

export function splitView(sidebar: View, main: View) {
  return group(sidebar, main).setAttribute("class", "etindi-split-view")
}

export function explorerSidebar(
  name: string,
  navigation: View,
  statuses: string[] = [],
  onFilter: (filters: {status: string, search: string}) => void = () => {},
) {
  const status = select(option("Status").setAttribute("value", ""), ...statuses.map(value => option(value).setAttribute("value", value)))
    .setAttribute("aria-label", "Status")
    .setAttribute("class", "etindi-explorer-control")
  const search = field("", {type: "search", autocomplete: "off"})
    .setAttribute("placeholder", "Search...")
    .setAttribute("aria-label", "Search")
    .setAttribute("class", "etindi-explorer-control")
  const emit = () => onFilter({status: status.el.value, search: search.el.value.trim()})
  status.el.addEventListener("change", emit)
  search.el.addEventListener("input", emit)
  return group(
    title(name).style({fontSize: "18px", margin: "0 0 16px"}),
    // group(span("Data Explorer"), icon("arrow").style({transform: "rotate(90deg)"})).style({
    //   display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0",
    //   marginBottom: "12px",
    //   fontSize: "16px", fontWeight: "700", textTransform: "uppercase",
    // }),
    muted("Filters").setAttribute("class", "etindi-eyebrow"),
    status,
    muted("Search").setAttribute("class", "etindi-eyebrow"),
    group(svg(["M21 21l-5-5", "M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z"]), search).setAttribute("class", "etindi-search"),
    muted("Categories").setAttribute("class", "etindi-eyebrow"),
    navigation,
  ).setAttribute("class", "etindi-explorer-sidebar")
}

export function explorerContent(...children: (View | string)[]) {
  return group(...children).setAttribute("class", "etindi-explorer-content")
}

export function sectionLabel(value: string) {
  return muted(value).setAttribute("class", "etindi-eyebrow")
}

export function explorerHeader(location: View, ...actions: View[]) {
  return group(location, group(...actions).setAttribute("class", "etindi-explorer-actions")).setAttribute("class", "etindi-explorer-header")
}

type NavigationItem = {label: string, active: boolean, open(): void}

export function explorerFolders(items: NavigationItem[]) {
  return group(...items.map(item => action("", item.open)
    .replaceChildren(label("folder", item.label))
    .setAttribute("aria-pressed", String(item.active))
    .setAttribute("class", "etindi-explorer-tab")))
    .setAttribute("class", "etindi-explorer-folders")
}

export function tileGrid(...items: View[]) {
  return group(...items).setAttribute("class", "etindi-tile-grid")
}

export function tile(name: string, onclick: () => void, meta = "") {
  return action("", onclick).replaceChildren(
    icon("arrow"), span(name),
    meta ? label("comment", `(${meta})`).style({color: palette.mutedText}) : "",
  ).setAttribute("class", "etindi-tile")
}

export function nestedTile(name: string, depth: number, onclick: () => void, meta = "") {
  return tile(name, onclick, meta)
    .style({paddingLeft: `${16 + depth * 32}px`})
    .setAttribute("data-depth", String(depth))
}

export type ItemTreeEntry = {
  id: string
  parentId?: string | undefined
  name: string
  meta?: string
  open(): void
  edit?(): void
  copy?(): void
  delete?(): void
}

export function itemTree(entries: ItemTreeEntry[], controls?: Region) {
  const view = region()
  const ids = new Set(entries.map(entry => entry.id))
  const children = new Map<string | undefined, ItemTreeEntry[]>()
  for (const entry of entries) {
    const parent = entry.parentId && ids.has(entry.parentId) ? entry.parentId : undefined
    children.set(parent, [...(children.get(parent) ?? []), entry])
  }
  const parents = new Set(entries.filter(entry => children.has(entry.id)).map(entry => entry.id))
  const expanded = new Set<string>()

  const draw = () => {
    const rows: View[] = []
    const visit = (parent: string | undefined, depth: number) => {
      for (const entry of children.get(parent) ?? []) {
        const hasChildren = parents.has(entry.id)
        const toggle = action(hasChildren ? (expanded.has(entry.id) ? "⌄" : "›") : "", event => {
          event.stopPropagation()
          if (expanded.has(entry.id)) expanded.delete(entry.id)
          else expanded.add(entry.id)
          draw()
        }).setAttribute("aria-label", `${expanded.has(entry.id) ? "Collapse" : "Expand"} ${entry.name}`)
          .setAttribute("aria-expanded", String(expanded.has(entry.id)))
          .setAttribute("class", "etindi-item-toggle")
        if (!hasChildren) toggle.el.disabled = true
        const itemActions = group(
          ...(entry.edit ? [iconAction(`Edit ${entry.name}`, "edit", entry.edit).setAttribute("class", "etindi-item-action")] : []),
          ...(entry.copy ? [iconAction(`Copy ${entry.name}`, "copy", entry.copy).setAttribute("class", "etindi-item-action")] : []),
          ...(entry.delete ? [iconAction(`Delete ${entry.name}`, "trash", entry.delete).setAttribute("class", "etindi-item-action etindi-item-delete")] : []),
        ).setAttribute("class", "etindi-item-actions")
        rows.push(group(
          toggle,
          action(entry.name, entry.open).setAttribute("class", "etindi-item-link"),
          itemActions,
          entry.meta ? label("comment", `(${entry.meta})`).style({color: palette.mutedText}) : "",
        ).style({paddingLeft: `${depth * 32}px`}).setAttribute("class", "etindi-item-row"))
        if (hasChildren && expanded.has(entry.id)) visit(entry.id, depth + 1)
      }
    }
    visit(undefined, 0)
    controls?.show(
      circleAction("Expand all", "expand", () => {
        for (const id of parents) expanded.add(id)
        draw()
      }),
      circleAction("Collapse all", "collapse", () => {
        expanded.clear()
        draw()
      }),
    )
    view.show(...rows)
  }
  draw()
  return view.setAttribute("class", "etindi-item-tree")
}

export function sourceReferences(references: {path: string[], excerpt: string}[], open: (path: string[]) => void) {
  return group(
    title("Sources").style({fontSize: "18px", margin: "0 0 12px"}),
    ...references.map(reference => action("", () => open(reference.path)).replaceChildren(
      group(label("file", reference.path.join(" / "))).setAttribute("class", "etindi-source-path"),
      readable(reference.excerpt).setAttribute("class", "etindi-source-excerpt"),
    ).setAttribute("class", "etindi-source-reference")),
  ).setAttribute("class", "etindi-source-references")
}

export function categoryMenu(items: NavigationItem[]) {
  return group(...items.map(item => action(item.label, item.open)
    .setAttribute("aria-current", item.active ? "page" : "false")))
    .setAttribute("class", "etindi-tree-menu")
}

export function propertyGrid(entries: {name: string, value: unknown}[]) {
  return group(...entries.map((entry, index) => group(
    muted(entry.name).style({margin: "0 0 4px", fontWeight: "600"}),
    typeof entry.value === "string" ? readable(entry.value) : wrappedCode(entry.value),
  ).style({padding: "13px 0", borderBottom: index < entries.length - 1 ? `1px solid ${palette.border}` : "none"}))).setAttribute("class", "etindi-property-grid")
}

export function formField(name: string, control: Field) {
  control.setAttribute("aria-label", name).style({width: "100%"})
  return labelElement(span(name).style({display: "block", color: palette.mutedText, fontSize: "13px", fontWeight: "600", marginBottom: "6px"}), control)
    .style({display: "block", margin: "14px 0"})
}

export function createForm(heading: string, labelText: string, submitText: string, submit: (value: string) => Promise<void>, cancel: () => void, report: (error: unknown) => void) {
  const control = field()
  return card(
    title(heading).style({fontSize: "17px", marginTop: "0"}),
    formField(labelText, control),
    toolbar(
      iconAction(submitText, "plus", async () => {
        const value = control.get().trim()
        if (!value) { report(new Error(`${labelText} is required.`)); return }
        try { await submit(value) }
        catch (error) { report(error) }
      }),
      action("Cancel", cancel),
    ),
  )
}

export function filterBar(
  filters: {key: string, label: string, placeholder: string, values: string[], hint?: string}[],
  onChange: (selection: Record<string, string>) => void,
) {
  const controls = filters.map(filter => {
    const suggestionsId = `filter-${filter.key}-${crypto.randomUUID()}`
    const control = field("", {type: "search", autocomplete: "off"})
    control.setAttribute("aria-label", filter.label)
    control.setAttribute("placeholder", filter.placeholder)
    control.setAttribute("list", suggestionsId)
    control.style({width: "100%", minWidth: "180px", maxWidth: "100%"})
    const suggestions = datalist(...filter.values.map(value => option().setAttribute("value", value)))
      .setAttribute("id", suggestionsId)
    return {filter, control, suggestions}
  })
  const selection = () => Object.fromEntries(controls.map(({filter, control}) => [filter.key, control.get().trim()]))
  for (const {control} of controls) control.el.addEventListener("input", () => onChange(selection()))
  return card(
    muted("Filter timeline").style({margin: "0 0 8px", fontWeight: "600"}),
    toolbar(...controls.map(({filter, control, suggestions}) => labelElement(
      span(filter.label).style({display: "block", color: palette.mutedText, fontSize: "12px", fontWeight: "600", marginBottom: "5px"}),
      control,
      suggestions,
      filter.hint ? muted(filter.hint).style({margin: "5px 0 0"}) : "",
    ).style({display: "block", flex: "1 1 220px"}))).style({margin: "0"}),
  )
}

export function linkRow(name: string, href: string, iconName: IconName) {
  return externalLink(name, href).replaceChildren(label(iconName, name), icon("arrow")).style({
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", color: palette.text,
    padding: "12px 4px", textDecoration: "none",
  })
}

export function dashboard(name: string, basePath: string) {
  const sections: {label: string, description: string, icon: IconName, path: string}[] = [
    {label: "Files", description: "Browse and edit the module contents.", icon: "folder", path: "files"},
    {label: "Etindi", description: "Continue a conversation or start a new one.", icon: "agent", path: "agents"},
    {label: "Etkom", description: "Open the module's rendered view.", icon: "sparkles", path: "view"},
    {label: "Timeline", description: "Review commits and inspect file changes.", icon: "clock", path: "timeline"},
  ]
  const result = workspace()
  return result.add(
    pageHeading(name, "Choose where you want to work.", "grid"),
    group(...sections.map(section => externalLink(section.label, `${basePath}/${section.path}`)
      .replaceChildren(
        group(icon(section.icon)).style({color: palette.accent, paddingTop: "3px"}),
        group(title(section.label).style({fontSize: "17px", margin: "0 0 4px"}), muted(section.description).style({margin: "0"})),
        group(icon("arrow")).style({marginLeft: "auto", color: palette.mutedText, alignSelf: "center"}),
      )
      .style({display: "flex", gap: "14px", padding: "18px", color: palette.text, background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: "0", textDecoration: "none", boxShadow: "none"})
    )).style({display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "14px"}),
  )
}
export function composer(send: (value: string) => Promise<void>, report: (error: unknown) => void) {
  const input = editor("").style({minHeight: "44px", padding: "8px", font: "inherit"})
  input.setAttribute("aria-label", "Message").setAttribute("placeholder", "Write a message…")
  const status = message()
  const submit = iconAction("Send", "arrow", async () => {
    const value = input.el.value.trim()
    input.el.value = "";
    if (!value) return
    input.el.disabled = true
    status.set("Waiting for response…")
    try { await send(value); status.set("") }
    catch (error) { status.set(""); report(error) }
    finally { input.el.disabled = false }
  })
  return card(input, toolbar(submit), status.view).setAttribute("class", "etindi-chat-composer")
}

export function statusArea() {
  type Severity = "success" | "warn" | "error"
  type Status = {id: number, message: string | View, severity: Severity}
  const priority: Record<Severity, number> = {success: 0, warn: 1, error: 2}
  const entries: Status[] = []
  const view = group().style({
    display: "none",
    position: "fixed",
    top: "var(--etindi-header-height)",
    left: "24px",
    right: "24px",
    zIndex: "300",
  }).setAttribute("class", "etindi-status-area")
  let nextId = 0

  const draw = () => {
    if (!entries.length) {
      view.replaceChildren().style({display: "none"})
      return
    }
    let selected = entries[0]!
    for (const entry of entries) {
      if (priority[entry.severity] >= priority[selected.severity]) selected = entry
    }
    const colors = ({
      success: [palette.success, palette.successText],
      warn: [palette.warning, palette.warningText],
      error: [palette.danger, palette.dangerText],
    } as const)[selected.severity]
    const more = entries.length - 1
    view.replaceChildren(
      group(
        selected.message,
        more ? span(` · ${more} more`) : "",
        iconAction("Dismiss", "close", () => {
          entries.splice(entries.findIndex(entry => entry.id === selected.id), 1)
          draw()
        }).style({marginLeft: "auto", padding: "4px"}),
        more ? action("Dismiss all", () => { entries.length = 0; draw() }) : "",
      ).style({display: "flex", alignItems: "center", gap: "8px", padding: "12px 14px"}),
    ).style({
      display: "block",
      border: `1px solid ${colors[1]}`,
      backgroundColor: colors[0],
      color: colors[1],
    })
  }

  return {
    view,
    clear() { entries.length = 0; draw() },
    add(message: string | View, severity: Severity = "warn") {
      if (typeof message === "string") {
        const duplicate = entries.findIndex(entry => entry.severity === severity && entry.message === message)
        if (duplicate >= 0) entries.splice(duplicate, 1)
      }
      entries.push({id: nextId++, message, severity})
      if (entries.length > 50) entries.shift()
      draw()
      return view
    },
  }
}

export function headerPicker(labelText: string, choices: {value: string, label: string}[], selected: string, change: (value: string) => void) {
  const control = select(...choices.map(choice => option(choice.label).setAttribute("value", choice.value)))
  control.el.value = selected
  control.el.addEventListener("change", () => change(control.el.value))
  return labelElement(span(labelText), control).setAttribute("class", "etindi-header-picker")
}

export function appShell() {
  const header = region()
  const status = statusArea()
  const page = region().style({padding: "0 24px 32px"}).setAttribute("class", "etindi-page") as Region
  body.append(
    group(externalLink("entrance", "/"), header).setAttribute("class", "etindi-app-header"),
    status.view,
    page,
  )
  return {page, status, header}
}
